import { Box, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';

import { useAnalytics } from '~/analytics';
import { sumCounts, tabLabelWithCount } from '~/components/common/TabCount';
import { TabPanel, useTabs } from '~/components/common/tabs';
import { TabContext, TabStrip } from '~/components/common/tabs/useTabs';
import { PropertyContracts } from '~/components/contracts/PropertyContracts';
import { selectedTabSearchParam } from '~/components/global/constants';
import { PropertyKnowledgeTab } from '~/components/knowledge/KnowledgeTimeline';
import { useKnowledgeEnabled } from '~/components/knowledge/useKnowledgeEnabled';
import { usePropertyRelatedCounts } from '~/components/portfolio/detailPage/usePropertyRelatedCounts';
import { PropertyCars } from '~/components/portfolio/PropertyCars';
import { PropertySpending } from '~/components/portfolio/PropertySpending';
import { PropertyTab } from '~/components/portfolio/propertyTabs';
import { PropertyTickets } from '~/components/portfolio/PropertyTickets';
import type { PropertyDetails } from '~/components/portfolio/types';
import { ServiceHistoryTab } from '~/components/serviceHistory/ServiceHistoryTab';
import { serviceHistoryTabLabel } from '~/components/serviceHistory/ServiceHistoryTabLabel';
import { shownInPeriod } from '~/components/tasks/taskFilters';
import { TasksTab } from '~/components/tasks/TasksTab';
import { useTasksEnabled } from '~/components/tasks/useTasksEnabled';
import { NeedsAttentionPanel } from '~/components/workQueue/NeedsAttentionPanel';
import { useNeedsAttentionItems } from '~/components/workQueue/useNeedsAttentionItems';
import { useGlobalFilterContext } from '~/contexts/globalFilterContext';
import { usePropertyKnowledge } from '~/data/hooks/useKnowledge';
import { useTasks } from '~/data/hooks/useTasks';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { pageBandColor, SURFACE_RADIUS } from '~/theme';

/**
 * Needs Attention first: the property's slice of the work queue is what there is to do
 * here, and everything after it is reference. Then Elevators — a property is a set of
 * them. No Overview — its KPI cards are the strip in the header band now, and a link
 * still carrying `?tab=overview` lands on the first tab.
 */
const allTabLabels = [
  PropertyTab.NeedsAttention,
  PropertyTab.Knowledge,
  PropertyTab.Tasks,
  PropertyTab.Cars,
  PropertyTab.VisitsCompletion,
  PropertyTab.Contracts,
  PropertyTab.Tickets,
  PropertyTab.Spending,
];

const isKnownTab = (tabLabels: PropertyTab[], tab: string | null): tab is PropertyTab =>
  tabLabels.includes(tab as PropertyTab);

/**
 * The tab *values* are lowercase, since they are what `?tab=` carries in links already
 * in the wild; the labels on screen come from `overrideLabels`, which is also where
 * each tab picks up its count.
 */
export function PropertyTabContainer({ property }: { property: PropertyDetails }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromSearchParams = searchParams.get(selectedTabSearchParam);
  const knowledgeEnabled = useKnowledgeEnabled();
  const tasksEnabled = useTasksEnabled();
  const tabLabels = useMemo(
    () =>
      allTabLabels.filter(
        (tab) =>
          (knowledgeEnabled || tab !== PropertyTab.Knowledge) &&
          (tasksEnabled || tab !== PropertyTab.Tasks),
      ),
    [knowledgeEnabled, tasksEnabled],
  );
  const terms = useLocaleTerms();
  const { track } = useAnalytics();
  const counts = usePropertyRelatedCounts(property.id);
  const needsAttention = useNeedsAttentionItems(property.id);
  const knowledge = usePropertyKnowledge(property.id);
  const tasks = useTasks({ propertyId: property.id });
  const {
    selectedFilters: { date: period },
  } = useGlobalFilterContext();
  const taskCount = tasks.tasks.filter((task) => shownInPeriod(task, period)).length;
  const currentKnowledgeCount = knowledge.entries.filter((e) => !e.supersededById).length;

  const onTabChange = useCallback(
    (tabName: string) => {
      // Tracked outside the `setSearchParams` updater — React calls updater callbacks twice
      // in StrictMode dev, which would double-fire the analytics event.
      const isChange = tabName !== searchParams.get(selectedTabSearchParam);
      setSearchParams(
        (prev) => {
          if (tabName !== prev.get(selectedTabSearchParam)) {
            prev.set(selectedTabSearchParam, tabName);
          }
          return prev;
        },
        // Switching tabs is not a place to come back to — Back should leave the
        // property, not walk the tabs that were opened on it.
        { replace: true },
      );
      if (isChange) {
        track('property_tab_switched', { property_id: property.id, tab: tabName });
      }
    },
    [searchParams, setSearchParams, track, property.id],
  );

  const { context, labelToTabId, selectTab } = useTabs(
    tabLabels,
    isKnownTab(tabLabels, tabFromSearchParams) ? tabFromSearchParams : PropertyTab.NeedsAttention,
    { onTabChange },
  );

  // React Router keeps the route mounted when only `:id` changes, so reset to the first
  // tab when a different property opens. A tab named in the URL is the reader asking for
  // one, and still wins.
  const openedProperty = useRef(property.id);
  useEffect(() => {
    const isNewProperty = property.id !== openedProperty.current;
    openedProperty.current = property.id;

    if (isKnownTab(tabLabels, tabFromSearchParams)) {
      selectTab(tabFromSearchParams);
    } else if (isNewProperty) {
      selectTab(PropertyTab.NeedsAttention);
    }
  }, [property.id, tabFromSearchParams, selectTab, tabLabels]);

  const overrideLabels = useMemo(
    () => ({
      [PropertyTab.NeedsAttention]: tabLabelWithCount('Needs Attention', needsAttention.count),
      [PropertyTab.Knowledge]: tabLabelWithCount(
        'Knowledge',
        knowledge.isLoading || knowledge.error ? undefined : currentKnowledgeCount,
      ),
      [PropertyTab.Cars]: tabLabelWithCount(terms.elevators, counts.elevators),
      [PropertyTab.VisitsCompletion]: serviceHistoryTabLabel(),
      [PropertyTab.Contracts]: tabLabelWithCount('Contracts', counts.contracts),
      [PropertyTab.Tickets]: tabLabelWithCount('Tickets', counts.tickets),
      // Undefined until every subtab is known, so it never counts up in steps.
      [PropertyTab.Spending]: tabLabelWithCount(
        'Spendings',
        sumCounts(counts.invoices, counts.proposals, counts.credits),
      ),
      [PropertyTab.Tasks]: tabLabelWithCount(
        'Custom Tasks',
        tasks.isLoading || tasks.error ? undefined : taskCount,
      ),
    }),
    [
      needsAttention.count,
      knowledge.isLoading,
      knowledge.error,
      currentKnowledgeCount,
      terms.elevators,
      counts.elevators,
      counts.contracts,
      counts.tickets,
      counts.invoices,
      counts.proposals,
      counts.credits,
      tasks.isLoading,
      tasks.error,
      taskCount,
    ],
  );

  return (
    // `minWidth: 0` here and on the panel row: a flex item defaults to `min-width: auto`,
    // so a data grid wider than the window stretches every ancestor instead of scrolling
    // inside its own viewport.
    <Stack flex={1} minWidth={0}>
      <TabContext.Provider value={context}>
        <TabStrip
          overrideLabels={overrideLabels}
          styles={{
            tabs: {
              backgroundColor: pageBandColor,
              px: { xs: 2, md: 3 },
              borderBottomLeftRadius: SURFACE_RADIUS,
              borderBottomRightRadius: SURFACE_RADIUS,
            },
          }}
        />
        {/* Must be a column flex container: a row one with overflow drops the cross-axis
          (bottom) padding of its scrolled content, clipping each tab's own pb.

          No `overflow: auto`: the page itself scrolls, up to the point where the strip
          above pins, and a scroll box here would take that scrolling off the page. */}
        <Box flex={1} minWidth={0} sx={{ display: 'flex', flexDirection: 'column' }}>
          <TabPanel tabId={labelToTabId[PropertyTab.NeedsAttention]}>
            <Box sx={{ p: 3, width: '100%' }}>
              <NeedsAttentionPanel
                items={needsAttention.items}
                isLoading={needsAttention.isLoading}
                isValidating={needsAttention.isValidating}
                snooze={needsAttention.snooze}
                refresh={needsAttention.refresh}
                emptyMessage="Nothing on this property needs attention right now."
              />
            </Box>
          </TabPanel>
          {knowledgeEnabled ? (
            <TabPanel tabId={labelToTabId[PropertyTab.Knowledge]}>
              <Box sx={{ p: 3, width: '100%' }}>
                <PropertyKnowledgeTab propertyId={property.id} />
              </Box>
            </TabPanel>
          ) : null}
          <TabPanel tabId={labelToTabId[PropertyTab.Cars]}>
            <Box sx={{ p: 3, width: '100%' }}>
              <PropertyCars propertyId={property.id} />
            </Box>
          </TabPanel>
          <TabPanel tabId={labelToTabId[PropertyTab.VisitsCompletion]}>
            <ServiceHistoryTab
              scope={{ property: property.id }}
              visitsEmptyMessage="This property has no elevators."
              callbacksEmptyMessage="This property has no elevators."
            />
          </TabPanel>
          <TabPanel tabId={labelToTabId[PropertyTab.Contracts]}>
            <PropertyContracts propertyId={property.id} />
          </TabPanel>
          <TabPanel tabId={labelToTabId[PropertyTab.Tickets]}>
            <Box sx={{ p: 3, width: '100%' }}>
              <PropertyTickets propertyId={property.id} />
            </Box>
          </TabPanel>
          <TabPanel tabId={labelToTabId[PropertyTab.Spending]}>
            <Box sx={{ p: 3, width: '100%' }}>
              <PropertySpending
                propertyId={property.id}
                classCounts={{
                  invoices: counts.invoices,
                  proposals: counts.proposals,
                  credits: counts.credits,
                }}
              />
            </Box>
          </TabPanel>
          {tasksEnabled ? (
            <TabPanel tabId={labelToTabId[PropertyTab.Tasks]}>
              <Box sx={{ p: 3, width: '100%' }}>
                <TasksTab
                  propertyId={property.id}
                  propertyName={property.name}
                  portfolioName={property.portfolio || null}
                />
              </Box>
            </TabPanel>
          ) : null}
        </Box>
      </TabContext.Provider>
    </Stack>
  );
}
